<template>
    <div class="appt-batch-card">
        <div class="batch-header">
            <span class="card-icon" aria-hidden="true">📅</span>
            <div class="batch-head-text">
                <div class="batch-title">{{ drafts.length }} randevu</div>
                <div class="batch-sub">
                    {{ creatable.length }} uygun<span v-if="blocked.length"> · {{ blocked.length }} çakışıyor</span>
                </div>
            </div>
        </div>

        <div v-if="common" class="batch-common">{{ common }}</div>

        <ul class="batch-list">
            <li v-for="(d, idx) in drafts" :key="idx" :class="['batch-row', { 'row-blocked': !d.canCreate }]">
                <span class="row-when">{{ fmtDate(d) }} · {{ fmtTime(d) }}</span>
                <span class="row-status" :title="d.blockReason || ''">{{ d.canCreate ? '✓' : '⚠' }}</span>
            </li>
        </ul>

        <div v-if="blocked.length" class="batch-note">
            <span aria-hidden="true">⚠</span>
            <span>{{ blocked.length }} randevu çakışıyor; yalnız uygun olanlar oluşturulacak.</span>
        </div>

        <div class="batch-actions">
            <button
                type="button"
                class="btn btn-primary"
                :disabled="saving || creatable.length === 0"
                @click="createAll">
                <span v-if="!saving">{{ creatable.length }} randevuyu oluştur</span>
                <span v-else class="spinner" aria-hidden="true"></span>
            </button>
            <button
                type="button"
                class="btn-ghost"
                :disabled="saving"
                @click="$emit('cancel')">
                İptal
            </button>
        </div>

        <div v-if="resultMsg" :class="['batch-result', resultError && 'is-error']">
            <span>{{ resultMsg }}</span>
        </div>
    </div>
</template>

<script>
    import axios from '../../services/index';

    const DAY_NAMES = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
    const MONTH_NAMES = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
                         'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

    function pad2(n) { return String(n).padStart(2, '0'); }

    export default {
        name: 'AppointmentBatchCard',
        props: {
            drafts: { type: Array, required: true }
        },
        data() {
            return {
                saving: false,
                resultMsg: '',
                resultError: false
            };
        },
        computed: {
            creatable() { return this.drafts.filter(d => d && d.canCreate); },
            blocked() { return this.drafts.filter(d => d && !d.canCreate); },
            common() {
                const d = this.drafts[0] || {};
                return [d.serviceName, d.staffName, d.roomName].filter(Boolean).join(' · ');
            }
        },
        methods: {
            fmtDate(d) {
                const x = new Date(d.startLocal);
                if (isNaN(x.getTime())) return '';
                return `${x.getDate()} ${MONTH_NAMES[x.getMonth()]} ${x.getFullYear()} ${DAY_NAMES[x.getDay()]}`;
            },
            fmtTime(d) {
                const s = new Date(d.startLocal);
                const e = new Date(d.endLocal);
                if (isNaN(s.getTime()) || isNaN(e.getTime())) return '';
                return `${pad2(s.getHours())}:${pad2(s.getMinutes())}–${pad2(e.getHours())}:${pad2(e.getMinutes())}`;
            },
            async createAll() {
                if (this.saving) return;
                const targets = this.creatable;
                if (!targets.length) return;
                this.saving = true;
                this.resultMsg = '';
                this.resultError = false;
                let ok = 0, fail = 0;
                for (const d of targets) {
                    try {
                        const res = await axios.post('/Appointment/New', d.upsertPayload);
                        if (res && res.data && res.data.success !== false) {
                            ok++;
                            this.$emit('confirmed-draft', { draft: d, result: res.data });
                        } else fail++;
                    } catch (e) { fail++; }
                }
                this.saving = false;
                if (fail === 0) {
                    this.$emit('confirm', { ok });
                } else {
                    this.resultError = true;
                    this.resultMsg = `${ok} randevu oluşturuldu, ${fail} tanesi oluşturulamadı.`;
                }
            }
        }
    };
</script>

<style scoped>
    .appt-batch-card {
        margin-top: 10px;
        padding: 14px;
        border-radius: 14px;
        background: linear-gradient(135deg, rgba(238, 242, 255, 0.85) 0%, rgba(250, 245, 255, 0.85) 100%);
        border: 1px solid rgba(99, 102, 241, 0.20);
        box-shadow: 0 2px 10px rgba(99, 102, 241, 0.08);
    }
    .batch-header { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
    .card-icon { font-size: 22px; line-height: 1; }
    .batch-title { font-size: var(--est-fs-body); font-weight: 600; color: #1e293b; }
    .batch-sub { font-size: var(--est-fs-meta); color: #4338ca; margin-top: 2px; }
    .batch-common {
        font-size: var(--est-fs-meta); font-weight: 500; color: #334155;
        margin-bottom: 8px; padding-bottom: 8px;
        border-bottom: 1px solid rgba(99, 102, 241, 0.14);
    }
    .batch-list { list-style: none; margin: 0 0 10px 0; padding: 0; display: grid; gap: 4px; max-height: 220px; overflow-y: auto; }
    .batch-row {
        display: flex; align-items: center; justify-content: space-between; gap: 8px;
        font-size: var(--est-fs-meta); color: #1e293b;
        padding: 4px 8px; border-radius: 8px; background: rgba(255, 255, 255, 0.55);
        font-variant-numeric: tabular-nums;
    }
    .batch-row.row-blocked { background: rgba(254, 226, 226, 0.55); color: #991b1b; }
    .row-status { font-weight: 700; }
    .batch-note {
        display: flex; align-items: center; gap: 8px;
        padding: 7px 10px; margin-bottom: 10px;
        background: rgba(254, 226, 226, 0.6); border: 1px solid rgba(239, 68, 68, 0.25);
        border-radius: 9px; font-size: var(--est-fs-meta); color: #991b1b;
    }
    .batch-actions { display: flex; flex-wrap: wrap; gap: 8px; }
    .btn-primary, .btn-ghost {
        display: inline-flex; align-items: center; justify-content: center;
        padding: 7px 14px; border-radius: 10px; font-size: var(--est-fs-meta); font-weight: 600;
        cursor: pointer;
        transition: transform 0.12s ease, box-shadow 0.15s ease, opacity 0.15s ease;
    }
    .btn-primary:hover:not(:disabled) { transform: translateY(-1px); }
    .btn-ghost { border: 1px solid transparent; background: transparent; color: #64748b; border-color: rgba(100, 116, 139, 0.20); }
    .btn-ghost:hover:not(:disabled) { background: rgba(100, 116, 139, 0.08); }
    .btn-ghost:disabled { opacity: 0.4; cursor: not-allowed; }
    .spinner {
        width: 13px; height: 13px;
        border: 2px solid rgba(255, 255, 255, 0.4); border-top-color: #fff;
        border-radius: 50%; animation: spin 0.7s linear infinite;
    }
    @keyframes spin { to { transform: rotate(360deg); } }
    .batch-result { margin-top: 10px; font-size: var(--est-fs-meta); color: #166534; }
    .batch-result.is-error { color: #991b1b; }
</style>
